// Splash / session lifecycle: idea entry, seed chips, the session library,
// reverse synthesis entry, and the resume/new/share/export actions.

import { fetchGrounding } from "../core/grounding";
import { createRoot, getNavStrategy, setNavStrategy, type NavStrategy } from "../core/navigation";
import { deleteSession, exportSession, listSessions, loadSession, restore } from "../core/persistence";
import { prefetchChildren } from "../core/prefetch";
import { reverseSynthesize } from "../core/reverse";
import { startReviewPolling } from "../core/reviews";
import { shareLink } from "../core/share";
import { resetSessionState, state } from "../core/state";
import { timeAgo, truncate } from "../lib/text";
import { $, el, renderThinking } from "./dom";
import { primeLiveDoc, resetLiveDoc } from "./livedoc";
import { renderAll } from "./render";
import { toast } from "./toast";

const SEEDS = [
  "A multi-tenant feature-flag service with per-flag targeting rules and <50ms edge evaluation.",
  "Rebuild our notification pipeline to support email, push, and in-app with dedup and rate limits.",
  "A URL shortener that handles 10k redirects/sec with custom domains and click analytics.",
  "Migrate our monolith's search from Postgres LIKE to a real search index without downtime.",
];

export function renderLibrary(): void {
  const wrap = $("#library");
  wrap.innerHTML = "";
  const sessions = listSessions();
  if (!sessions.length) return;
  wrap.appendChild(el("div", "lib-title", "Previous explorations"));
  sessions.slice(0, 8).forEach((s) => {
    const row = el("div", "lib-row");
    const txt = el("div", "lib-txt");
    txt.appendChild(el("div", "lib-idea", truncate(s.idea || "(untitled)", 80)));
    txt.appendChild(el("div", "lib-meta", `${s.nodeCount} decision${s.nodeCount === 1 ? "" : "s"} · ${timeAgo(s.updatedAt)}`));
    row.appendChild(txt);
    const del = el("button", "lib-del", "✕");
    del.title = "delete this exploration";
    del.onclick = (e) => { e.stopPropagation(); deleteSession(s.id); renderLibrary(); };
    row.appendChild(del);
    row.onclick = () => { if (loadSession(s.id)) resumeSession(); };
    wrap.appendChild(row);
  });
}

export function initSplash(): void {
  const input = $("#ideaInput") as HTMLTextAreaElement;
  const chips = $("#seedChips");
  SEEDS.forEach((s) => {
    const c = el("button", "chip-seed", s.split(" ").slice(0, 5).join(" ") + "…");
    c.title = s;
    c.onclick = () => { input.value = s; input.focus(); ($("#goBtn") as HTMLButtonElement).disabled = false; };
    chips.appendChild(c);
  });
  input.addEventListener("input", () => {
    ($("#goBtn") as HTMLButtonElement).disabled = !input.value.trim();
    // a paste that looks like a full doc unlocks reverse synthesis
    $("#reverseBtn").style.display = input.value.trim().length > 400 ? "" : "none";
  });
  input.addEventListener("keydown", (e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void start(); });
  $("#goBtn").onclick = () => void start();
  $("#reverseBtn").onclick = () => void startReverse();
}

async function start(): Promise<void> {
  const idea = ($("#ideaInput") as HTMLTextAreaElement).value.trim();
  if (!idea) return;
  state.idea = idea;
  $("#ideaTxt").textContent = idea;
  enterApp();

  // fire grounding in parallel with the first navigator call
  void fetchGrounding();

  renderThinking($("#nodeCard"), "mapping the design space…", 4);
  try {
    await createRoot();
    renderAll();
    prefetchChildren(state.nodes.get(state.rootId!)!);
  } catch {
    $("#nodeCard").innerHTML = "";
    $("#nodeCard").appendChild(el("div", "question", "Couldn't reach the navigator. Check the connection and reload."));
  }
}

/** Reverse synthesis (M2): existing doc → recovered graph → validate/improve. */
async function startReverse(): Promise<void> {
  const doc = ($("#ideaInput") as HTMLTextAreaElement).value.trim();
  if (doc.length < 200) { toast("paste a full design doc first"); return; }
  enterApp();
  $("#ideaTxt").textContent = "decomposing pasted doc…";
  renderThinking($("#nodeCard"), "decomposing the doc into its decision graph…", 5);
  try {
    const { branches, decisions } = await reverseSynthesize(doc);
    $("#ideaTxt").textContent = state.idea;
    void fetchGrounding();
    renderAll();
    toast(`recovered ${decisions} decisions across ${branches} branches — press V to challenge them, S to re-synthesise`);
  } catch {
    $("#nodeCard").innerHTML = "";
    $("#nodeCard").appendChild(el("div", "question", "Couldn't decompose that doc — try trimming it to its core sections."));
  }
}

function enterApp(): void {
  $("#splash").classList.add("hide");
  $("#app").classList.add("show");
  $("#keybar").style.display = "flex";
  setTimeout(() => { $("#splash").style.display = "none"; }, 400);
  startReviewPolling();   // pick up agent design reviews as they stream in
}

/** Resume a persisted session (skip the splash, land where you left off). */
export function resumeSession(): void {
  $("#ideaTxt").textContent = state.idea;
  enterApp();
  renderAll();
  primeLiveDoc();
  const cur = state.nodes.get(state.currentId!);
  if (cur) prefetchChildren(cur);
  if (!state.grounding.length) void fetchGrounding();
}

export function newSession(): void {
  resetSessionState();
  $("#app").classList.remove("show");
  $("#keybar").style.display = "none";
  $("#splash").style.display = "";
  $("#splash").classList.remove("hide");
  ($("#ideaInput") as HTMLTextAreaElement).value = "";
  ($("#ideaInput") as HTMLTextAreaElement).focus();
  ($("#goBtn") as HTMLButtonElement).disabled = true;
  renderLibrary();
  resetLiveDoc();
}

export async function doShare(): Promise<void> {
  if (!state.rootId) { toast("nothing to share yet"); return; }
  try {
    const link = await shareLink();
    await navigator.clipboard.writeText(link);
    toast(`share link copied — ${Math.round(link.length / 1024 * 10) / 10} KB, whole graph included`);
  } catch { toast("couldn't build the share link in this browser"); }
}

export function doExport(): void {
  if (!state.rootId) { toast("nothing to export yet"); return; }
  const blob = new Blob([exportSession()], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = el("a") as HTMLAnchorElement;
  a.href = url;
  a.download = `lattice-${(state.idea || "session").slice(0, 32).replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "")}.json`;
  a.click();
  URL.revokeObjectURL(url);
  toast("trace exported as JSON");
}

export function restoreOnBoot(): boolean { return restore(); }

// ---- navigation strategy picker ---------------------------------------------
export function renderNavPick(): void {
  const wrap = $("#navPick");
  [...wrap.querySelectorAll("button")].forEach((b) => b.remove());
  (["depth", "breadth", "smart"] as NavStrategy[]).forEach((s) => {
    const b = el("button", "nav-opt" + (getNavStrategy() === s ? " active" : ""), s);
    b.onclick = () => { setNavStrategy(s); renderNavPick(); toast(`navigation: ${s}-first`); };
    wrap.appendChild(b);
  });
}
